import { CartesianScaleOptions, Chart, ChartType, Plugin, Scale, Tick } from 'chart.js';

export interface ICategoryAxisTicksOptions {
  /**
   * @description Enables/disables the plugin. Default value is true
   */
  display?: boolean;
  /**
   * @description Id of the axis scale the ticks are drawn for. Default value is 'x'
   */
  axisId?: string;
  /**
   * @description Color of the tick lines. Falls back to the axis border color.
   */
  color?: string;
  /**
   * @description Width of the tick lines. Default value is 1
   */
  lineWidth?: number;
  /**
   * @description Length (px) of the big ticks drawn on displayed labels. Default value is 8
   */
  majorTickLength?: number;
  /**
   * @description Length (px) of the small ticks drawn between displayed labels. Default value is 4
   */
  minorTickLength?: number;
  /**
   * @description Maximum amount of small ticks drawn between two consecutive displayed
   * labels. The interval is split into equal parts that land on bar centers; the amount
   * is reduced by one until the gaps are equal and aligned to the dataset, so it may end
   * up smaller than this value. Default value is 6
   */
  maxMinorTicks?: number;
  /**
   * @description When enabled, the amount of displayed labels (major ticks) is reduced in
   * `afterBuildTicks` so the gap between them can be split into equal minor ticks. This
   * avoids "prime" gaps between labels that would otherwise show no minor ticks. Requires
   * the axis to keep consecutive category ticks (works alongside `autoSkip`). Default value
   * is false
   */
  adjustMajorTicks?: boolean;
  /**
   * @description Target amount of displayed labels used when `adjustMajorTicks` is enabled.
   * The final amount may differ slightly to keep equal, divisible gaps. Default value is 5
   */
  majorTicksLimit?: number;
  /**
   * @description Custom predicate deciding which bars are major. By default a bar is
   * considered major when its label is displayed (not empty).
   */
  isMajorTick?: (label: string, index: number) => boolean;
}

declare module 'chart.js' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface PluginOptionsByType<TType extends ChartType = ChartType> {
    categoryAxisTicks?: ICategoryAxisTicksOptions;
  }
}

const getDisplayedLabel = (scale: Scale, tick: Tick): string => {
  const raw = tick.label;
  if (Array.isArray(raw)) {
    return raw.join('').trim();
  }
  if (typeof raw === 'string') {
    return raw.trim();
  }
  const fallback = scale.getLabelForValue(tick.value);
  return typeof fallback === 'string' ? fallback.trim() : '';
};

/**
 * For an interval spanning `gap` bars between two displayed labels, returns the integer
 * bar-step that produces as many equally spaced small ticks as possible without exceeding
 * `max`. Starts from `max` and reduces it by one until the step lands exactly on bar
 * centers (equal, dataset-aligned gaps). Returns 0 when no such split exists.
 */
const getEqualBarStep = (gap: number, max: number): number => {
  for (let count = Math.min(max, gap - 1); count >= 1; count--) {
    if (gap % (count + 1) === 0) {
      return gap / (count + 1);
    }
  }
  return 0;
};

/**
 * Picks the amount of bars between two displayed labels so that the gap can be split into
 * equal minor ticks. Starts from the spacing that yields `targetCount` labels and looks
 * for the nearest value that is not a "prime" gap (i.e. that getEqualBarStep can divide).
 * Any integer multiple of the result stays divisible, so a later `autoSkip` pass keeps the
 * gap minor-friendly.
 */
const chooseMajorStep = (span: number, targetCount: number, maxMinor: number): number => {
  const ideal = Math.max(2, Math.round(span / Math.max(targetCount - 1, 1)));
  for (let delta = 0; delta <= span; delta++) {
    for (const step of delta === 0 ? [ideal] : [ideal - delta, ideal + delta]) {
      if (step >= 2 && step <= span && getEqualBarStep(step, maxMinor) > 0) {
        return step;
      }
    }
  }
  return ideal;
};

export const categoryAxisTicksPlugin: Plugin = {
  id: 'categoryAxisTicks',

  // Reduce the amount of displayed labels before autoSkip runs, so the gap between them
  // stays divisible and the minor ticks always fit (avoids empty "prime" gaps).
  afterBuildTicks: (_chart: Chart, args: { scale: Scale }, options: ICategoryAxisTicksOptions) => {
    if (!(options.display ?? true) || !options.adjustMajorTicks) {
      return;
    }

    const { axisId = 'x', maxMinorTicks = 6, majorTicksLimit = 5 } = options;
    const scale = args.scale;
    if (!scale || scale.id !== axisId) {
      return;
    }

    const ticks = scale.ticks;
    if (!ticks || ticks.length <= majorTicksLimit) {
      return;
    }

    const step = chooseMajorStep(ticks.length - 1, majorTicksLimit, maxMinorTicks);
    if (step <= 1) {
      return;
    }

    scale.ticks = ticks.filter((_tick, index) => index % step === 0);
  },

  // Drawn before the grid and datasets so the ticks sit on the background, behind everything.
  beforeDraw: (chart: Chart, _args, options: ICategoryAxisTicksOptions) => {
    if (!(options.display ?? true)) {
      return;
    }

    const {
      axisId = 'x',
      lineWidth = 1,
      majorTickLength = 8,
      minorTickLength = 4,
      maxMinorTicks = 6,
      isMajorTick,
    } = options;

    const scale = chart.scales[axisId];
    if (!scale) {
      return;
    }

    const color = options.color ?? (scale.options as CartesianScaleOptions).border?.color?.toString() ?? '#000';

    const min = Math.ceil(scale.min);
    const max = Math.floor(scale.max);

    // Category indices whose label is displayed -> big (major) ticks.
    const majorIndices = new Set<number>();
    scale.ticks.forEach(tick => {
      const label = getDisplayedLabel(scale, tick);
      const isMajor = isMajorTick ? isMajorTick(label, tick.value) : label !== '';
      if (isMajor) {
        majorIndices.add(tick.value);
      }
    });

    const ctx = chart.ctx;
    const y = scale.top;

    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;

    // Ticks are anchored to bar centers: with the bar category axis `offset` enabled,
    // getPixelForValue(index) resolves to the middle of the bar for that index.
    const drawTick = (index: number, length: number): void => {
      const x = scale.getPixelForValue(index);
      if (!Number.isFinite(x)) {
        return;
      }
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x, y + length);
      ctx.stroke();
    };

    const majors = [...majorIndices].filter(index => index >= min && index <= max).sort((a, b) => a - b);
    majors.forEach(index => drawTick(index, majorTickLength));

    // Split every interval between two displayed labels into equal bar-steps so the small
    // ticks are evenly spaced and centered on bars; the amount shrinks until they align.
    let leadStep = 0;
    let lastStep = 0;
    for (let i = 0; i < majors.length - 1; i++) {
      const start = majors[i];
      const step = getEqualBarStep(majors[i + 1] - start, maxMinorTicks);

      if (i === 0) {
        leadStep = step;
      }
      lastStep = step;

      if (step <= 0) {
        continue;
      }
      for (let index = start + step; index < majors[i + 1]; index += step) {
        drawTick(index, minorTickLength);
      }
    }

    // Keep the same rhythm on the bars outside the displayed labels (leading/trailing).
    if (majors.length && leadStep > 0) {
      for (let index = majors[0] - leadStep; index >= min; index -= leadStep) {
        drawTick(index, minorTickLength);
      }
    }
    if (majors.length && lastStep > 0) {
      for (let index = majors[majors.length - 1] + lastStep; index <= max; index += lastStep) {
        drawTick(index, minorTickLength);
      }
    }

    ctx.restore();
  },
};
